/**
 * Dropdown Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import { clsx } from "clsx";
import type React from "react";
import type { ReactElement, Ref } from "react";
import { cloneElement, isValidElement, useCallback, useEffect, useRef, useState } from "react";
import type { DropdownProps } from "./Dropdown.types";

// ---------------------------------------------------------------------------
// Inline ref-merge helper
// Merges multiple refs (callback or object) into a single callback ref so the
// cloned trigger element can satisfy both `triggerRef` and any existing ref
// the consumer placed on the element.
// ---------------------------------------------------------------------------
function mergeRefs<T>(...refs: (Ref<T> | null | undefined)[]): (node: T | null) => void {
  return (node: T | null) => {
    for (const ref of refs) {
      if (ref == null) continue;
      if (typeof ref === "function") {
        ref(node);
      } else {
        // MutableRefObject — safe to assign
        (ref as React.MutableRefObject<T | null>).current = node;
      }
    }
  };
}

export function Dropdown({ trigger, items, onSelect, align = "left", className }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  // Widened from HTMLButtonElement — cloned trigger may be any focusable element.
  const triggerRef = useRef<HTMLElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Indices of selectable (non-disabled) items — keyboard navigation skips disabled rows.
  const enabledIndices = items.map((item, i) => (item.disabled ? -1 : i)).filter((i) => i >= 0);

  const close = useCallback((returnFocus = false) => {
    setOpen(false);
    setHighlightIndex(-1);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, close]);

  // Roving focus: move DOM focus to the highlighted menu item so screen-reader
  // and keyboard users track the active option (WCAG 2.4.7 / ARIA menu pattern).
  useEffect(() => {
    if (open && highlightIndex >= 0) {
      itemRefs.current[highlightIndex]?.focus();
    }
  }, [open, highlightIndex]);

  function openMenu(toIndex: number) {
    setOpen(true);
    setHighlightIndex(toIndex);
  }

  function moveHighlight(direction: 1 | -1) {
    const pos = enabledIndices.indexOf(highlightIndex);
    const nextPos =
      pos === -1
        ? direction === 1
          ? 0
          : enabledIndices.length - 1
        : (pos + direction + enabledIndices.length) % enabledIndices.length;
    setHighlightIndex(enabledIndices[nextPos] ?? -1);
  }

  // Accepts a generic keyboard event so it can be used on any element (button,
  // span, div) when the trigger is cloned.
  function handleTriggerKeyDown(e: React.KeyboardEvent<HTMLElement>) {
    switch (e.key) {
      case "Enter":
      case " ":
      case "ArrowDown":
        e.preventDefault();
        openMenu(enabledIndices[0] ?? -1);
        break;
      case "ArrowUp":
        e.preventDefault();
        openMenu(enabledIndices[enabledIndices.length - 1] ?? -1);
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          close();
        }
        break;
    }
  }

  function handleMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveHighlight(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveHighlight(-1);
        break;
      case "Home":
        e.preventDefault();
        setHighlightIndex(enabledIndices[0] ?? -1);
        break;
      case "End":
        e.preventDefault();
        setHighlightIndex(enabledIndices[enabledIndices.length - 1] ?? -1);
        break;
      case "Escape":
        e.preventDefault();
        close(true);
        break;
      case "Tab":
        // Tab leaves the menu entirely (ARIA menu pattern) — close it.
        close();
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // Render the trigger
  // If the trigger is a single React element we clone it, injecting the
  // interaction handlers and ARIA attributes directly onto it. This avoids
  // the invalid nested-button HTML that the former <button className="contents">
  // wrapper produced when the consumer passed a <button> as the trigger.
  //
  // Fallback: if the trigger is not a single element (string, number, fragment,
  // array) we keep the original wrapper-button pattern.
  // ---------------------------------------------------------------------------
  let renderedTrigger: React.ReactNode;

  if (isValidElement(trigger)) {
    const el = trigger as ReactElement<React.HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> }>;

    // Compose the existing ref on the element (if any) with our triggerRef.
    const existingRef = (el as { ref?: Ref<HTMLElement> }).ref ?? null;
    const mergedRef = mergeRefs<HTMLElement>(triggerRef, existingRef);

    // Compose click handlers.
    const existingOnClick = el.props.onClick as React.MouseEventHandler<HTMLElement> | undefined;
    const handleClick: React.MouseEventHandler<HTMLElement> = (e) => {
      existingOnClick?.(e);
      open ? close() : openMenu(-1);
    };

    // Compose keydown handlers.
    const existingOnKeyDown = el.props.onKeyDown as React.KeyboardEventHandler<HTMLElement> | undefined;
    const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (e) => {
      existingOnKeyDown?.(e);
      handleTriggerKeyDown(e);
    };

    // Extra a11y props for non-button elements (WCAG 2.1.1 / 4.1.2).
    const isNativeButton = el.type === "button";
    // Use Record<string, unknown> to avoid fighting HTMLAttributes<T> structural
    // constraints — cloneElement accepts any extra props at runtime.
    const a11yExtra: Record<string, unknown> = isNativeButton
      ? { type: (el.props as React.ButtonHTMLAttributes<HTMLButtonElement>).type ?? "button" }
      : {
          role: el.props.role ?? "button",
          tabIndex: el.props.tabIndex ?? 0,
        };

    // biome-ignore lint/suspicious/noExplicitAny: cloneElement's generic prop param must accept ANY consumer-supplied trigger element's prop shape (role/tabIndex/aria-* injected regardless of the underlying component's declared props); `unknown` breaks cloneElement's overload resolution here.
    renderedTrigger = cloneElement(el as ReactElement<any>, {
      ...a11yExtra,
      ref: mergedRef,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      "aria-expanded": open,
      "aria-haspopup": "menu",
    });
  } else {
    // Fallback for string/number/fragment/array triggers.
    renderedTrigger = (
      <button
        ref={triggerRef as React.RefObject<HTMLButtonElement>}
        type="button"
        onClick={() => (open ? close() : openMenu(-1))}
        onKeyDown={handleTriggerKeyDown}
        aria-expanded={open}
        aria-haspopup="menu"
        className="contents"
      >
        {trigger}
      </button>
    );
  }

  return (
    <div ref={containerRef} className={clsx("relative inline-block", className)}>
      {/* aria-haspopup="menu" tells AT this control opens a menu (WCAG 4.1.2).
          For single-element triggers the prop is injected via cloneElement above;
          for primitive triggers the wrapper button carries it. */}
      {renderedTrigger}

      {/* aria-hidden hides the menu from AT when visually hidden (opacity/visibility alone is not enough) */}
      <div
        aria-hidden={!open}
        onKeyDown={handleMenuKeyDown}
        className={clsx(
          "absolute top-[calc(100%+0.5rem)] z-[150]",
          // Literal white/gray-800 surface — do NOT use `bg-surface` here. It emits
          // `background-color: var(--color-bg-surface)` with NO fallback, and that var
          // is stripped from the React SPA apps' Tailwind v4 build. The literal ramp
          // keeps fallbacks (`var(--color-gray-800,#1e293b)`) so dark mode renders true-dark.
          "bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700",
          "rounded-md shadow-lg",
          "min-w-[200px] py-1",
          "transition-all duration-150",
          open ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2",
          align === "right" ? "right-0" : "left-0"
        )}
        role="menu"
      >
        {items.map((item, index) => (
          <button
            key={item.value}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            type="button"
            role="menuitem"
            tabIndex={-1}
            disabled={item.disabled}
            onClick={() => {
              if (!item.disabled) {
                onSelect(item.value);
                close(true);
              }
            }}
            onMouseEnter={() => !item.disabled && setHighlightIndex(index)}
            className={clsx(
              "w-full flex items-center gap-3",
              "py-2 px-4",
              "text-sm text-left",
              "transition-colors duration-150",
              // Focus ring restored for keyboard users (WCAG 2.4.7).
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400",
              // danger items → literal red scale (auto-correct in both themes);
              // default items → literal gray ramp with explicit dark: pairs.
              item.danger
                ? "text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                : "text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700",
              highlightIndex === index &&
                !item.disabled &&
                (item.danger ? "bg-red-50 dark:bg-red-900/20" : "bg-gray-100 dark:bg-gray-700"),
              item.disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            {item.icon && (
              <span className="shrink-0" aria-hidden="true">
                {item.icon}
              </span>
            )}
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

Dropdown.displayName = "Dropdown";
